// ABOUTME: The withdrawal in two steps: the reason the talent writes in their own words, then a confirmation, so a
// ABOUTME: move that cannot be undone is never one tap away.
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { AlertDialog } from 'components/dialog';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';

interface Props {
  isOpen: boolean;
  isWithdrawing: boolean;
  /** Copy for a withdrawal the backend refused, shown where the talent asked for it rather than on the page behind. */
  errorMessage?: string;
  onClose: () => void;
  onConfirm: (reasonText: string) => void;
}

const enum Step {
  reason = 'reason',
  confirm = 'confirm'
}

export const WithdrawDialog = ({
  isOpen,
  isWithdrawing,
  errorMessage,
  onClose,
  onConfirm
}: Props) => {
  const [step, setStep] = useState<Step>(Step.reason);
  const [reasonText, setReasonText] = useState('');

  // A closed dialog keeps nothing: the next withdrawal starts on the reason, blank, whatever happened to the last one.
  useEffect(() => {
    if (!isOpen) {
      setStep(Step.reason);
      setReasonText('');
    }
  }, [isOpen]);

  // Spaces are not a reason, and that is the whole rule: pro-core refuses a blank reason and takes anything else, so
  // the dialog asks for exactly what the withdrawal needs. A length of our own would stop a talent on "too far".
  const isReasonEnough = reasonText.trim().length > 0;

  const failure = errorMessage && (
    <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
      {errorMessage}
    </Alert>
  );

  if (step === Step.confirm) {
    return (
      <AlertDialog
        isOpen={isOpen}
        title={getTrans('applications.withdraw.confirmTitle')}
        onClose={() => setStep(Step.reason)}
        onConfirm={() => onConfirm(reasonText)}
        cancelLabel={getTrans('general.back')}
        confirmLabel={getTrans('applications.withdraw.submit')}
        confirmColor="error"
        isConfirmDisabled={isWithdrawing}
        // Backing out here closes the dialog a step later, and reopening it resets the mutation, which frees the
        // confirm button again while the first POST is still on its way. The way out waits for the answer.
        isCancelDisabled={isWithdrawing}
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          <Text variant="body2">
            {getTrans('applications.withdraw.confirmBody')}
          </Text>
        </DialogContent>
        {failure}
      </AlertDialog>
    );
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      title={getTrans('applications.withdraw.title')}
      onClose={onClose}
      onConfirm={() => setStep(Step.confirm)}
      confirmLabel={getTrans('general.continue')}
      isConfirmDisabled={!isReasonEnough}
    >
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={reasonText}
          onChange={event => setReasonText(event.target.value)}
          label={getTrans('applications.withdraw.reasonLabel')}
          helperText={getTrans('applications.withdraw.reasonHelper')}
        />
      </DialogContent>
      {failure}
    </AlertDialog>
  );
};
